import { useMemo } from "react";
import { AnimatePresence, motion } from "framer-motion";
import useProductLicenseForm from "core/hooks/useProductLicenseForm";
import useBundleFilters from "core/hooks/useBundleFilters";
import ButtonLink from "core/components/ButtonLink";
import ProductBundleCard from "modules/admin/OrgProductLicenseForm/ProductBundleCard";
import {
  PRODUCT_BUNDLES,
  DEPLOYMENT_STATUSES,
} from "modules/admin/OrgProductLicenseForm/constants";
import { NotificationHeader } from "modules/admin/OrgProductLicenseForm/styles";
import { Row } from "core/styles";

const ProductLicenseConfigurePage = () => {
  const { formState, licenseDeployment } = useProductLicenseForm();
  const { selectedBundleIds } = useBundleFilters();

  const {
    getDynamicProduct,
    getProductIdsEnabled,
    getProductsEnabled,
    getChangedEnabledFields,
    getPreparedLicenses,
  } = formState;

  const productsEnabled = getProductsEnabled();

  const handleProductEnabledToggleChange = (productId, isEnabled) => {
    const product = getDynamicProduct(productId);
    product.page.isEnabled.setValue(isEnabled);
  };

  const configuredBundles = useMemo(() => {
    return productsEnabled
      .reduce((bundleIds, product) => {
        const productBundleId = product.meta?.bundle?.id;

        if (bundleIds.includes(productBundleId)) {
          return bundleIds;
        }

        return bundleIds.concat(productBundleId);
      }, [])
      .map((bundleId) => PRODUCT_BUNDLES[bundleId]);
  }, [productsEnabled]);

  const allConfiguredBundlesAreInView = configuredBundles.every((bundle) => {
    return selectedBundleIds.includes(bundle.id);
  });

  return (
    <AnimatePresence mode="popLayout">
      {!allConfiguredBundlesAreInView && (
        <motion.div
          key="hidden-products-notice"
          initial={{ opacity: 0 }}
          animate={{
            opacity: 1,
            transition: {
              duration: 0.75,
              ease: "linear",
            },
          }}
          exit={{
            opacity: 0,
            transition: {
              duration: 0,
            },
          }}
          layout
        >
          <NotificationHeader>
            You currently have some configured products that are being filtered
            from view. Their visibility does not affect their configurations or
            whether they are included in a deployment.
          </NotificationHeader>
        </motion.div>
      )}

      <div>
        <ProductBundleCard
          preparedUpdates={getChangedEnabledFields()}
          productsEnabled={getProductIdsEnabled()}
          handleProductToggle={handleProductEnabledToggleChange}
          isReadOnly={[
            DEPLOYMENT_STATUSES.IN_PROGRESS,
            DEPLOYMENT_STATUSES.QUEUED,
          ].includes(licenseDeployment?.status)}
        />

        <motion.div layout transition={{ duration: ".35", ease: "easeInOut" }}>
          <Row
            style={{
              justifyContent: "flex-end",
              gap: "20px",
            }}
          >
            <ButtonLink to="../confirm" disabled={!getPreparedLicenses()}>
              Review Deployment
            </ButtonLink>
          </Row>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default ProductLicenseConfigurePage;
